<template>
    <BaseCard v-if="visible && uploadControl?.enabled.value && matchingReview" theme="cream" radius="lgr" class="my-4 w-full">
        <div class="flex items-start justify-between gap-4 mb-3">
            <h2 class="nadia-chat-bold text-ink">{{ t("moments.documents.dialog.title") }}</h2>
            <BaseButton theme="ghost" size="sm" icon-only type="button" :aria-label="t('common.close')" class="shrink-0" :disabled="isDismissing" @click="dismiss">
                <i class="bi bi-x-lg" aria-hidden="true"></i>
            </BaseButton>
        </div>
        <div class="flex flex-col gap-4 body text-ink-muted">
            <p>{{ t("moments.documents.dialog.intro") }} {{ t("moments.documents.dialog.examplesLabel") }}</p>
            <ul class="list-disc ml-5 mb-3">
                <li v-for="example in examples" :key="example">{{ example }}</li>
            </ul>
            <AdditionalDocumentUploadSection
                :is-disabled="isDismissing || uploadControl.remaining.value === 0"
                :remaining-file-count="uploadControl.remaining.value"
                :upload-type="DOCUMENT_UPLOAD_TYPES.OTHER"
                :files="pendingFiles"
                @uploading="uploadControl.setBusy?.($event)"
                @document-uploaded="addFiles"
                @document-removed="removeFile" />
            <p v-if="pendingFiles.length" class="body-small">{{ t("chat.performancePicker.documents.pendingMessage") }}</p>
        </div>
        <BaseToast v-model:open="showError" theme="error" :description="t('chat.performancePicker.documents.dismissFailed')" />
    </BaseCard>
</template>
<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { ReviewDocumentCardParams, ReviewDocumentCardRequest, ReviewDocumentCardResponse } from "~vue/api/generated/chat";
import type { ChatInputFile, UploadedFile } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import AdditionalDocumentUploadSection from "~vue/Moments/AdditionalDocumentUploadSection.vue";
import { DOCUMENT_UPLOAD_TYPES } from "~vue/Moments/constants.js";
import { computed, inject, nextTick, onUnmounted, type Ref, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ data: { action_params: ReviewDocumentCardParams; action_state?: { dismissed?: boolean } | null }; messageId: number; lineIdx: number }>();
const uploadControl = inject<{
    enabled: Ref<boolean>;
    context: Ref<{ sessionUuid: string; reviewConversationId?: string }>;
    remaining: Ref<number>;
    files: Ref<ChatInputFile[]>;
    addFiles: (files: UploadedFile[]) => void;
    removeFile: (file: ChatInputFile) => void;
    focus: () => void;
    setBusy?: (busy: boolean) => void;
}>("reviewDocumentUpload");
const updateActionState = inject<((payload: { message_id: number; lineIdx: number; action_state: { dismissed: boolean } }) => void) | null>("updateActionState", null);
const { t } = useI18n();
const visible = computed(() => !props.data.action_state?.dismissed && !dismissed.value);
const dismissed = ref(false);
const pendingFiles = computed(() => uploadControl?.files.value ?? []);
const showError = ref(false);
const origin = uploadControl?.context.value;
let active = true;
onUnmounted(() => {
    active = false;
    uploadControl?.setBusy?.(false);
});
const matchingReview = computed(() => uploadControl?.context.value.reviewConversationId === props.data.action_params.review_conversation_id);
const examples = computed(() =>
    props.data.action_params.examples.length
        ? props.data.action_params.examples
        : [t("moments.documents.dialog.examples.goals"), t("moments.documents.dialog.examples.feedbackNotes"), t("moments.documents.dialog.examples.previousReview")],
);
const payload = ref<ReviewDocumentCardRequest>({ review_conversation_id: props.data.action_params.review_conversation_id });
const {
    execute,
    error,
    isFetching: isDismissing,
} = useFetch(() => `/v1/chat/performance-management/${origin?.sessionUuid}/document-card/dismiss`, { immediate: false })
    .post(payload)
    .json<ReviewDocumentCardResponse>();
const isCurrent = () =>
    active &&
    !dismissed.value &&
    uploadControl?.enabled.value &&
    origin?.sessionUuid === uploadControl?.context.value.sessionUuid &&
    origin?.reviewConversationId === uploadControl?.context.value.reviewConversationId;
function addFiles(files: UploadedFile | UploadedFile[]) {
    if (!isCurrent()) {
        return;
    }
    const selected = Array.isArray(files) ? files : [files];
    if (!selected.length) {
        return;
    }
    uploadControl?.addFiles(selected);
}
function removeFile(file: ChatInputFile) {
    if (isCurrent()) {
        uploadControl?.removeFile(file);
    }
}
async function dismiss() {
    if (isDismissing.value || !isCurrent()) {
        return;
    }
    await execute();
    if (error.value) {
        showError.value = true;
        return;
    }
    if (!isCurrent()) {
        return;
    }
    dismissed.value = true;
    updateActionState?.({ message_id: props.messageId, lineIdx: props.lineIdx, action_state: { dismissed: true } });
    await nextTick();
    uploadControl?.focus();
}
</script>
